import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';

import { isNewAifcExperienceEnabledNoExposure } from '../../ui/utils/new-aifc-experience-enabled';

import type { AIContentPositionEntry, SessionContextPluginState } from './types';

/**
 * CSS class applied to the inline decoration that shades an undone
 * Post Stream Review segment. Styled in `GlobalWrapper` (global editor styles)
 * with `token('color.background.neutral')`, mirroring how the other AI editor
 * decoration classes (e.g. `ai-selection-highlight`) are styled there.
 */
export const UNDONE_SEGMENT_DECORATION_CLASS = 'ai-undone-segment-highlight';

/**
 * Resolves the entries to shade, mirroring the PostStreamReview screen so the
 * decoration reads the same entries whose `appliedIndex` undo/redo mutates:
 *   - Gate ON + step-through: the flattened `diffSegments` leaves (skip accepted;
 *     fall back to the entry itself when it has no leaves).
 *   - Otherwise: the top-level `streamReviewSegments` (with `isUndoneSegment`
 *     collapsing leaf state for aggregate), falling back to the coarse
 *     `aiContentPositions` only when there are no fine segments yet.
 *
 * Using `aiContentPositions` in aggregate mode was the original bug: undo/redo
 * never writes `appliedIndex` there, so nothing was shaded.
 */
const getWorkingSet = (
	pluginState: SessionContextPluginState,
): AIContentPositionEntry[] | undefined => {
	const topLevel = pluginState.streamReviewSegments;
	// No fine segments yet (e.g. a transient pre-bootstrap state): the coarse
	// commit ranges are all we have.
	if (!topLevel || topLevel.length === 0) {
		return pluginState.aiContentPositions;
	}

	const isStepThrough = pluginState.currentStreamReviewMode === 'stepThrough';

	// Gate ON + step-through: shade the flattened diff-level leaves, matching the
	// screen's `displaySegments` flatten.
	if (isStepThrough && fg('platform_editor_ai_diff_based_segmenting')) {
		return topLevel
			.filter((entry) => !entry.isAccepted)
			.flatMap((entry) =>
				entry.diffSegments ? entry.diffSegments.filter((leaf) => !leaf.isAccepted) : [entry],
			);
	}

	// Gate OFF (either mode) or gate ON + aggregate: shade the top-level entries.
	return topLevel;
};

/**
 * A segment is "undone" when `appliedIndex === 0` (its original pre-AI content).
 * Accepted segments and zero-width ranges never qualify (nothing to shade, and
 * ProseMirror rejects an empty inline decoration).
 *
 * An aggregate parent carries undo state on its `diffSegments` leaves, so it is
 * "undone" only when every non-accepted leaf is itself undone.
 */
const isUndoneSegment = (entry: AIContentPositionEntry): boolean => {
	if (entry.isAccepted || entry.endPos <= entry.startPos) {
		return false;
	}
	if (entry.diffSegments && entry.diffSegments.length > 0) {
		const reviewableLeaves = entry.diffSegments.filter((leaf) => !leaf.isAccepted);
		return reviewableLeaves.length > 0 && reviewableLeaves.every((leaf) => leaf.appliedIndex === 0);
	}
	return entry.appliedIndex === 0;
};

/**
 * Builds the inline gray-background decoration set for every undone
 * segment in the current working set.
 *
 * Returns `DecorationSet.empty` unless the New AIFC Editor Experience is enabled
 * (no-exposure check — this runs in the render path) and the session is actively
 * in Post Stream Review, so the decoration is torn down for free when the review
 * closes (status leaves `postStreamReview`, e.g. on `RESET`).
 *
 * Positions are read straight from plugin state, which `apply()` already remaps
 * on every doc change, so the ranges are always current. Out-of-bounds ranges
 * are defended against `doc.content.size` so a transient mid-transaction state
 * can never throw.
 */
export const buildUndoneSegmentDecorationSet = (
	doc: PMNode,
	pluginState: SessionContextPluginState,
): DecorationSet => {
	if (pluginState.status !== 'postStreamReview') {
		return DecorationSet.empty;
	}

	if (!isNewAifcExperienceEnabledNoExposure()) {
		return DecorationSet.empty;
	}

	const workingSet = getWorkingSet(pluginState);
	if (!workingSet || workingSet.length === 0) {
		return DecorationSet.empty;
	}

	const docSize = doc.content.size;
	const decorations: Decoration[] = [];

	workingSet.forEach((entry) => {
		if (!isUndoneSegment(entry)) {
			return;
		}
		const from = entry.startPos;
		const to = entry.endPos;
		// Defend against a range that a transient state left outside the current
		// document — an inline decoration outside `[0, docSize]` throws.
		if (from < 0 || to > docSize || from >= to) {
			return;
		}
		decorations.push(
			Decoration.inline(
				from,
				to,
				{ class: UNDONE_SEGMENT_DECORATION_CLASS, 'data-testid': UNDONE_SEGMENT_DECORATION_CLASS },
				// Key on the (non-overlapping) range, not the array index: accepting a
				// segment removes it from the front of the working set and would shift
				// every later index, making ProseMirror destroy/recreate all surviving
				// decorations. A position-based key keeps their identity stable.
				{ key: `${UNDONE_SEGMENT_DECORATION_CLASS}-${from}-${to}` },
			),
		);
	});

	if (decorations.length === 0) {
		return DecorationSet.empty;
	}

	return DecorationSet.create(doc, decorations);
};

/**
 * Creates a memoised decoration provider for the `ai-session-context`
 * plugin's `props.decorations`.
 *
 * `props.decorations(state)` is called by the view on EVERY `updateState` — not
 * just doc changes — so a naive rebuild would allocate a fresh `DecorationSet`
 * (and defeat ProseMirror's decoration diffing) on pure navigation transactions
 * that don't change the doc. We memoise on the working-set array reference plus
 * `status`:
 *   - Navigation with no doc change (a plain `POST_STREAM_REVIEW` re-open) keeps
 *     the same working-set array reference, so the cached set is returned
 *     untouched — nothing is recreated.
 *   - Undo / redo / accept and doc-change remaps all produce a NEW working-set
 *     array reference in `apply()`/reducer, so the set is rebuilt at the fresh
 *     positions exactly when — and only when — it needs to be.
 *
 * The memo is per-plugin-instance (closure), so it never leaks across editors.
 */
export const createUndoneSegmentDecorationProvider = (): ((
	doc: PMNode,
	pluginState: SessionContextPluginState,
) => DecorationSet) => {
	let cachedWorkingSet: AIContentPositionEntry[] | undefined;
	let cachedStatus: SessionContextPluginState['status'];
	let cachedDoc: PMNode | undefined;
	let cachedSet: DecorationSet = DecorationSet.empty;

	// Reference-stabilise the derived working set: on the gate-on step-through path
	// `getWorkingSet` flattens the `diffSegments` leaves into a fresh array each
	// call, which would break the `workingSet === cachedWorkingSet` memo below and
	// rebuild the set every transaction. Reuse the previous array while its inputs
	// (source array, mode, status) are unchanged.
	let derivedWorkingSet: AIContentPositionEntry[] | undefined;
	let derivedSourceKey: AIContentPositionEntry[] | undefined;
	let derivedModeStatusKey: string | undefined;
	const getStableWorkingSet = (
		pluginState: SessionContextPluginState,
	): AIContentPositionEntry[] | undefined => {
		const hasFineSegments =
			!!pluginState.streamReviewSegments && pluginState.streamReviewSegments.length > 0;
		// Key on the inputs `getWorkingSet` actually reads: the fine segments when
		// present (else the coarse fallback), plus mode/status which select its
		// branch. The unused fallback is excluded so its transient empty arrays
		// don't force needless rebuilds.
		const sourceKey = hasFineSegments
			? pluginState.streamReviewSegments
			: pluginState.aiContentPositions;
		const modeStatusKey = `${pluginState.status}|${pluginState.currentStreamReviewMode}`;
		if (derivedSourceKey === sourceKey && derivedModeStatusKey === modeStatusKey) {
			return derivedWorkingSet;
		}
		derivedWorkingSet = getWorkingSet(pluginState);
		derivedSourceKey = sourceKey;
		derivedModeStatusKey = modeStatusKey;
		return derivedWorkingSet;
	};

	return (doc: PMNode, pluginState: SessionContextPluginState): DecorationSet => {
		const workingSet = getStableWorkingSet(pluginState);
		// The working-set reference and status fully determine the decoration set,
		// but the doc identity must match too: a doc change produces a new working
		// set via the remap in `apply()`, yet we still guard on `doc` so a
		// decoration set is never returned against a stale document.
		if (
			workingSet === cachedWorkingSet &&
			pluginState.status === cachedStatus &&
			doc === cachedDoc
		) {
			return cachedSet;
		}

		cachedSet = buildUndoneSegmentDecorationSet(doc, pluginState);
		cachedWorkingSet = workingSet;
		cachedStatus = pluginState.status;
		cachedDoc = doc;
		return cachedSet;
	};
};
